import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { pointsAPI } from '../../api/pointsAPI';
import { logger } from '../../utils/logger';
import { transferGiftCard } from './giftCardsSlice';

// const mockTransactions = [
//   {
//     id: 1,
//     from_user_id: null,
//     to_user_id: 1,
//     amount: "100.00",
//     transaction_type: "INIT",
//     reference_id: null,
//     created_at: "2024-12-29T22:11:11.744597Z",
//     status: "SUCCESS"
//   },
//   {
//     id: 2,
//     from_user_id: 1,
//     to_user_id: 2,
//     amount: "25.00",
//     transaction_type: "TRANSFER",
//     reference_id: null,
//     created_at: "2024-12-29T23:48:27.530191Z",
//     status: "SUCCESS"
//   },
//   {
//     id: 3,
//     from_user_id: 1,
//     to_user_id: 3,
//     amount: "15.00",
//     transaction_type: "TRANSFER",
//     reference_id: null,
//     created_at: "2024-12-29T23:50:15.123456Z",
//     status: "SUCCESS"
//   },
//   {
//     id: 4,
//     from_user_id: 2,
//     to_user_id: 1,
//     amount: "50.00",
//     transaction_type: "PURCHASE",
//     reference_id: "order_001",
//     created_at: "2024-12-29T23:55:22.789012Z",
//     status: "SUCCESS"
//   },
//   {
//     id: 5,
//     from_user_id: 3,
//     to_user_id: 1,
//     amount: "30.00",
//     transaction_type: "SALE",
//     reference_id: "order_002",
//     created_at: "2024-12-30T00:05:33.456789Z",
//     status: "SUCCESS"
//   }
// ];

// Async thunks
export const getPointsBalance = createAsyncThunk(
  'points/getBalance',
  async (_, { rejectWithValue }) => {
    try {
      const data = await pointsAPI.getBalance();
      logger.log('Points API response:', data);
      return data;
    } catch (error) {
      logger.error('Error fetching points balance:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch points balance');
    }
  }
);

export const getPointsTransactions = createAsyncThunk(
  'points/getTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const data = await pointsAPI.getTransactions();
      return data;
    } catch (error) {
      logger.error('Error fetching points transactions:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch points transactions');
    }
  }
);

export const initializePoints = createAsyncThunk(
  'points/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const response = await pointsAPI.initializePoints();
      toast.success('Wallet initialized successfully.');
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to initialize wallet');
    }
  }
);

export const fetchVouchers = createAsyncThunk(
  'points/fetchVouchers',
  async (_, { rejectWithValue }) => {
    try {
      const data = await pointsAPI.getVouchers();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to load vouchers');
    }
  }
);

export const redeemVoucher = createAsyncThunk(
  'points/redeemVoucher',
  async (voucherType, { rejectWithValue }) => {
    try {
      const data = await pointsAPI.redeemVoucher(voucherType);
      toast.success(`Voucher issued! Your promo code: ${data.code}`);
      return data;
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Redeem failed';
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const transferPoints = createAsyncThunk(
  'points/transfer',
  async (transferData, { rejectWithValue }) => {
    try {
      const data = await pointsAPI.transferPoints({
        username: transferData.username,
        phone_number: transferData.phone_number,
        amount: transferData.amount,
      });
      logger.log('Transfer API response:', data);
      toast.success(`Successfully transferred ${transferData.amount} points to @${transferData.username}!`);
      return data;
    } catch (error) {
      logger.error('Error transferring points:', error);
      const errorData = error.response?.data || {};
      const errorMessage = errorData.error || errorData.message || error.message || 'Failed to transfer points';
      
      // If user not found, show referral message
      if (errorData.user_not_found) {
        toast.error(errorData.message || errorMessage, {
          autoClose: 6000, // Show longer for referral message
        });
      } else if (errorData.phone_mismatch) {
        toast.error(errorData.error || errorMessage);
      } else {
        toast.error(errorMessage);
      }
      
      return rejectWithValue({
        error: errorMessage,
        user_not_found: errorData.user_not_found || false,
        phone_mismatch: errorData.phone_mismatch || false,
        username: errorData.username,
        message: errorData.message
      });
    }
  }
);

const initialState = {
  balance: null,
  walletId: null,
  transactions: [],
  loading: false,
  error: null
};

const pointsSlice = createSlice({
  name: 'points',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetPoints: (state) => {
      state.balance = null;
      state.walletId = null;
      state.transactions = [];
      state.vouchers = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Balance
      .addCase(getPointsBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPointsBalance.fulfilled, (state, action) => {
        state.loading = false;
        logger.log('Points balance fulfilled:', action.payload);
        state.balance = action.payload.balance;
        if (action.payload.wallet_id != null) {
          state.walletId = action.payload.wallet_id;
        }
      })
      .addCase(getPointsBalance.rejected, (state, action) => {
        state.loading = false;
        logger.log('Points balance rejected:', action.payload);
        state.error = action.payload;
      })
      
      // Get Transactions
      .addCase(getPointsTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPointsTransactions.fulfilled, (state, action) => {
        state.loading = false;
        // Filter out any undefined or invalid transactions
        state.transactions = Array.isArray(action.payload) 
          ? action.payload.filter(transaction => transaction && transaction.transaction_type)
          : [];
      })
      .addCase(getPointsTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Initialize Points
      .addCase(initializePoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializePoints.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload.balance;
      })
      .addCase(initializePoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Transfer Points
      .addCase(transferPoints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferPoints.fulfilled, (state, action) => {
        state.loading = false;
        const nb = action.payload?.new_balance ?? action.payload?.newBalance;
        if (nb != null) state.balance = nb;
      })
      .addCase(transferPoints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchVouchers.pending, (state) => {
        state.vouchersLoading = true;
      })
      .addCase(fetchVouchers.fulfilled, (state, action) => {
        state.vouchersLoading = false;
        state.vouchers = action.payload || [];
      })
      .addCase(fetchVouchers.rejected, (state) => {
        state.vouchersLoading = false;
      })
      .addCase(redeemVoucher.pending, (state) => {
        state.loading = true;
      })
      .addCase(redeemVoucher.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.new_balance != null) {
          state.balance = action.payload.new_balance;
        }
        if (action.payload?.code) {
          state.vouchers = [
            {
              id: action.payload.code,
              voucher_type: action.payload.voucher_type,
              points_cost: action.payload.points_cost,
              code: action.payload.code,
              status: 'ACTIVE',
              created_at: new Date().toISOString(),
            },
            ...state.vouchers,
          ];
        }
      })
      .addCase(redeemVoucher.rejected, (state) => {
        state.loading = false;
      })
      .addCase(transferGiftCard.fulfilled, (state, action) => {
        const code = action.payload?.code;
        if (!code) return;
        const normalized = String(code).toLowerCase();
        state.vouchers = state.vouchers.filter(
          (v) => v.code !== code && String(v.code || '').toLowerCase() !== normalized
        );
      });
  }
});

export const { clearError, resetPoints } = pointsSlice.actions;
export default pointsSlice.reducer;
